import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Timer } from "three/addons/misc/Timer.js";
import GUI from "lil-gui";
import { Sky } from "three/addons/objects/Sky.js";

/**
 * Base
 */
// Debug
const gui = new GUI();
gui.hide();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader();

/**
 * Floor textures
 */
// Color
const floorAlphaTexture = textureLoader.load("./floor/alpha.webp");
const floorColorTexture = textureLoader.load(
	"./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_diff_1k.webp",
);
floorColorTexture.colorSpace = THREE.SRGBColorSpace;
floorColorTexture.repeat.set(8, 8);
floorColorTexture.wrapS = THREE.RepeatWrapping;
floorColorTexture.wrapT = THREE.RepeatWrapping;

// AOR
const floorARMTexture = textureLoader.load(
	"./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_arm_1k.webp",
);
floorARMTexture.repeat.set(8, 8);
floorARMTexture.wrapS = THREE.RepeatWrapping;
floorARMTexture.wrapT = THREE.RepeatWrapping;

// Normal
const floorNormalTexture = textureLoader.load(
	"./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_nor_gl_1k.webp",
);
floorNormalTexture.repeat.set(8, 8);
floorNormalTexture.wrapS = THREE.RepeatWrapping;
floorNormalTexture.wrapT = THREE.RepeatWrapping;

// Displacement
const floorDisplacementTexture = textureLoader.load(
	"./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_disp_1k.webp",
);
floorDisplacementTexture.repeat.set(8, 8);
floorDisplacementTexture.wrapS = THREE.RepeatWrapping;
floorDisplacementTexture.wrapT = THREE.RepeatWrapping;

/**
 * Walls Texture
 */
// Color
const wallColorTexture = textureLoader.load(
	"./wall/castle_brick_broken_06_1k/castle_brick_broken_06_diff_1k.webp",
);
wallColorTexture.colorSpace = THREE.SRGBColorSpace;

// AOR
const wallARMTexture = textureLoader.load(
	"./wall/castle_brick_broken_06_1k/castle_brick_broken_06_arm_1k.webp",
);
wallARMTexture.repeat.set(8, 8);
wallARMTexture.wrapS = THREE.RepeatWrapping;
wallARMTexture.wrapT = THREE.RepeatWrapping;

// Normal
const wallNormalTexture = textureLoader.load(
	"./wall/castle_brick_broken_06_1k/castle_brick_broken_06_nor_gl_1k.webp",
);
wallNormalTexture.repeat.set(8, 8);
wallNormalTexture.wrapS = THREE.RepeatWrapping;
wallNormalTexture.wrapT = THREE.RepeatWrapping;

/**
 * Roof Texture
 */
// Color
const roofColorTexture = textureLoader.load(
	"./roof/roof_slates_02_1k/roof_slates_02_diff_1k.webp",
);
roofColorTexture.colorSpace = THREE.SRGBColorSpace;
roofColorTexture.repeat.set(3, 1);
roofColorTexture.wrapS = THREE.RepeatWrapping;

// AORM
const roofARMTexture = textureLoader.load(
	"./roof/roof_slates_02_1k/roof_slates_02_arm_1k.webp",
);
roofARMTexture.repeat.set(3, 1);
roofARMTexture.wrapS = THREE.RepeatWrapping;

// Normal
const roofNormalTexture = textureLoader.load(
	"./roof/roof_slates_02_1k/roof_slates_02_nor_gl_1k.webp",
);
roofNormalTexture.repeat.set(3, 1);
roofNormalTexture.wrapS = THREE.RepeatWrapping;

/**
 * Bushes Texture
 */
const bushColorTexture = textureLoader.load(
	"./bush/leaves_forest_ground_1k/leaves_forest_ground_diff_1k.webp",
);
bushColorTexture.colorSpace = THREE.SRGBColorSpace;
bushColorTexture.repeat.set(2, 1);
bushColorTexture.wrapS = THREE.RepeatWrapping;

// AORM
const bushARMTexture = textureLoader.load(
	"./bush/leaves_forest_ground_1k/leaves_forest_ground_arm_1k.webp",
);
bushARMTexture.repeat.set(2, 1);
bushARMTexture.wrapS = THREE.RepeatWrapping;

// Normal
const bushNormalTexture = textureLoader.load(
	"./bush/leaves_forest_ground_1k/leaves_forest_ground_nor_gl_1k.webp",
);
bushNormalTexture.repeat.set(2, 1);
bushNormalTexture.wrapS = THREE.RepeatWrapping;

/**
 * Graves Texture
 */
// Color
const graveColorTexture = textureLoader.load(
	"./grave/plastered_stone_wall_1k/plastered_stone_wall_diff_1k.webp",
);
graveColorTexture.colorSpace = THREE.SRGBColorSpace;
graveColorTexture.repeat.set(0.3, 0.4);

// AORM
const graveARMTexture = textureLoader.load(
	"./grave/plastered_stone_wall_1k/plastered_stone_wall_arm_1k.webp",
);
graveARMTexture.repeat.set(0.3, 0.4);

// Normal
const graveNormalTexture = textureLoader.load(
	"./grave/plastered_stone_wall_1k/plastered_stone_wall_nor_gl_1k.webp",
);
graveNormalTexture.repeat.set(0.3, 0.4);

/**
 * Door Texture
 */
// Color
const doorColorTexture = textureLoader.load("./door/color.webp");
doorColorTexture.colorSpace = THREE.SRGBColorSpace;

// Alpha
const doorAlphaTexture = textureLoader.load("./door/alpha.webp");

// AO
const doorAmbientOcclusionTexture = textureLoader.load(
	"./door/ambientOcclusion.webp",
);

// Height
const doorHeightTexture = textureLoader.load("./door/height.webp");

// Metalness
const doorMetalnessTexture = textureLoader.load("./door/metalness.webp");

// Normal
const doorNormalTexture = textureLoader.load("./door/normal.webp");

// Roughness
const doorRoughnessTexture = textureLoader.load("./door/roughness.webp");

/**
 * House
 */
// House Conatainer
const house = new THREE.Group();
scene.add(house);

// Walls
const walls = new THREE.Mesh(
	new THREE.BoxGeometry(4, 2.5, 4),
	new THREE.MeshStandardMaterial({
		map: wallColorTexture,
		aoMap: wallARMTexture,
		metalnessMap: wallARMTexture,
		roughnessMap: wallARMTexture,
		normalMap: wallNormalTexture,
	}),
);
walls.position.y += 2.5 / 2;
house.add(walls);

// Roofs
const roof = new THREE.Mesh(
	new THREE.ConeGeometry(3.5, 1.5, 4),
	new THREE.MeshStandardMaterial({
		map: roofColorTexture,
		aoMap: roofARMTexture,
		metalnessMap: roofARMTexture,
		roughnessMap: roofARMTexture,
		normalMap: roofNormalTexture,
	}),
);
roof.position.y = 2.5 + 0.75;
roof.rotation.y = Math.PI * 0.25;
house.add(roof);

// Door
const door = new THREE.Mesh(
	new THREE.PlaneGeometry(2.2, 2.2, 80, 80),
	new THREE.MeshStandardMaterial({
		map: doorColorTexture,
		transparent: true,
		alphaMap: doorAlphaTexture,
		aoMap: doorAmbientOcclusionTexture,
		displacementMap: doorHeightTexture,
		displacementScale: 0.15,
		displacementBias: -0.04,
		metalnessMap: doorMetalnessTexture,
		roughnessMap: doorRoughnessTexture,
		normalMap: doorNormalTexture,
	}),
);
door.position.y = 1;
door.position.z = 2 + 0.001;
house.add(door);

// Bushes
const bushGeometry = new THREE.SphereGeometry(1, 16, 16);
const bushMaterial = new THREE.MeshStandardMaterial({
	map: bushColorTexture,
	aoMap: bushARMTexture,
	metalnessMap: bushARMTexture,
	roughnessMap: bushARMTexture,
	normalMap: bushNormalTexture,
	color: "#ccffcc",
});

const bush1 = new THREE.Mesh(bushGeometry, bushMaterial);
bush1.position.set(0.8, 0.2, 2.2);
bush1.scale.set(0.5, 0.5, 0.5);
bush1.rotation.x = -0.75;

const bush2 = new THREE.Mesh(bushGeometry, bushMaterial);
bush2.scale.set(0.25, 0.25, 0.25);
bush2.position.set(1.4, 0.1, 2.1);
bush2.rotation.x = -0.75;

const bush3 = new THREE.Mesh(bushGeometry, bushMaterial);
bush3.scale.set(0.4, 0.4, 0.4);
bush3.position.set(-0.8, 0.1, 2.2);
bush3.rotation.x = -0.75;

const bush4 = new THREE.Mesh(bushGeometry, bushMaterial);
bush4.scale.set(0.15, 0.15, 0.15);
bush4.position.set(-1, 0.05, 2.6);
bush4.rotation.x = -0.75;

house.add(bush1, bush2, bush3, bush4);

// Graves
const graveGeometry = new THREE.BoxGeometry(0.6, 0.8, 0.2);
const graveMaterial = new THREE.MeshStandardMaterial({
	map: graveColorTexture,
	aoMap: graveARMTexture,
	metalnessMap: graveARMTexture,
	roughnessMap: graveARMTexture,
	normalMap: graveNormalTexture,
});

// Grave Group
const graves = new THREE.Group();
scene.add(graves);

for (let i = 0; i < 30; i++) {
	const angle = Math.random() * Math.PI * 2;
	const radius = 3 + Math.random() * 4;
	const x = Math.sin(angle) * radius;
	const z = Math.cos(angle) * radius;

	// Mesh
	const grave = new THREE.Mesh(graveGeometry, graveMaterial);
	grave.position.x = x;
	grave.position.z = z;
	grave.position.y = Math.random() * 0.4;
	grave.rotation.x = (Math.random() - 0.5) * 0.4;
	grave.rotation.y = (Math.random() - 0.5) * 0.4;
	grave.rotation.z = (Math.random() - 0.5) * 0.4;

	// Add grave to graves
	graves.add(grave);
}

// Floor
const floor = new THREE.Mesh(
	new THREE.PlaneGeometry(20, 20, 100, 100),
	new THREE.MeshStandardMaterial({
		transparent: true,
		alphaMap: floorAlphaTexture,
		map: floorColorTexture,
		aoMap: floorARMTexture,
		metalnessMap: floorARMTexture,
		roughnessMap: floorARMTexture,
		normalMap: floorNormalTexture,
		displacementMap: floorDisplacementTexture,
		displacementScale: 0.3,
		displacementBias: -0.2,
	}),
);
floor.rotation.x = -Math.PI * 0.5;
scene.add(floor);

gui.add(floor.material, "displacementScale")
	.min(0)
	.max(1)
	.step(0.001)
	.name("Floor Displacement Scale");

gui.add(floor.material, "displacementBias")
	.min(-1)
	.max(1)
	.step(0.001)
	.name("Floor Displacement Bias");

/**
 * Lights
 */
// Ambient light
const ambientLight = new THREE.AmbientLight("#86cdff", 0.25);
scene.add(ambientLight);

// Directional light
const directionalLight = new THREE.DirectionalLight("#86cdff", 1);
directionalLight.position.set(3, 2, -8);
scene.add(directionalLight);

// Door Light
const doorLight = new THREE.PointLight("#ff7d46", 5);
doorLight.position.set(0, 2.2, 2.5);
scene.add(doorLight);

/**
 * Ghosts
 */
const ghost1 = new THREE.PointLight("#8800ff", 10);
const ghost2 = new THREE.PointLight("#ff0088", 10);
const ghost3 = new THREE.PointLight("#ff0000", 10);
scene.add(ghost1, ghost2, ghost3);

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	75,
	sizes.width / sizes.height,
	0.1,
	100,
);
camera.position.x = 4;
camera.position.y = 2;
camera.position.z = 5;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Shadows
 */
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

// Cast and Receive
directionalLight.castShadow = true;

ghost1.castShadow = true;
ghost2.castShadow = true;
ghost3.castShadow = true;

walls.castShadow = true;
walls.receiveShadow = true;

roof.castShadow = true;

floor.receiveShadow = true;

for (const grave of graves.children) {
	grave.castShadow = true;
	grave.receiveShadow = true;
}

// Mappings
directionalLight.shadow.mapSize.width = 256;
directionalLight.shadow.mapSize.height = 256;
directionalLight.shadow.camera.top = 8;
directionalLight.shadow.camera.right = 8;
directionalLight.shadow.camera.bottom = -8;
directionalLight.shadow.camera.left = -8;
directionalLight.shadow.camera.near = 1;
directionalLight.shadow.camera.far = 20;

ghost1.shadow.mapSize.width = 256;
ghost1.shadow.mapSize.height = 256;
ghost1.shadow.camera.far = 10;

ghost2.shadow.mapSize.width = 256;
ghost2.shadow.mapSize.height = 256;
ghost2.shadow.camera.far = 10;

ghost3.shadow.mapSize.width = 256;
ghost3.shadow.mapSize.height = 256;
ghost3.shadow.camera.far = 10;

/**
 * Sky
 */
const sky = new Sky();
sky.scale.set(100, 100, 100);
scene.add(sky);

sky.material.uniforms["turbidity"].value = 10;
sky.material.uniforms["rayleigh"].value = 4;
sky.material.uniforms["mieCoefficient"].value = 0.1;
sky.material.uniforms["mieDirectionalG"].value = 0.95;
sky.material.uniforms["sunPosition"].value.set(0.3, -0.038, -0.95);

/**
 * Fog
 */
scene.fog = new THREE.FogExp2("#04343f", 0.1);

/**
 * Animate
 */
const timer = new Timer();

const tick = () => {
	// Timer
	timer.update();
	const elapsedTime = timer.getElapsed();

	// Ghost 1 stuff
	const ghost1Angle = elapsedTime * 0.5;
	ghost1.position.x = Math.cos(ghost1Angle) * 4;
	ghost1.position.z = Math.sin(ghost1Angle) * 4;
	ghost1.position.y =
		Math.sin(ghost1Angle) *
		Math.sin(ghost1Angle * 2.34) *
		Math.sin(ghost1Angle * 3.45);

	// Ghost 2 stuff
	const ghost2Angle = -elapsedTime * 0.38;
	ghost2.position.x = Math.cos(ghost2Angle) * 5;
	ghost2.position.z = Math.sin(ghost2Angle) * 5;
	ghost2.position.y =
		Math.sin(ghost2Angle) *
		Math.sin(ghost2Angle * 2.34) *
		Math.sin(ghost2Angle * 3.45);

	// Ghost 3 stuff
	const ghost3Angle = elapsedTime * 0.23;
	ghost3.position.x = Math.cos(ghost3Angle) * 6;
	ghost3.position.z = Math.sin(ghost3Angle) * 6;
	ghost3.position.y =
		Math.sin(ghost3Angle) *
		Math.sin(ghost3Angle * 2.34) *
		Math.sin(ghost3Angle * 3.45);

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
